/**
 * @file UserEditModal.tsx
 * @description Модалка «Редактировать пользователя» — имя + смена пароля.
 *
 * Бэкенд:
 *   - PATCH /admin/users/:id       — displayName/isActive (PLATFORM only)
 *   - POST  /admin/users/:id/password — установка нового пароля (PLATFORM only)
 *
 * Email менять нельзя (это идентификатор для логина и инвайтов) — отображается
 * read-only. Пароль необязателен: если поле пустое — пароль не трогаем.
 */

import { useEffect, useState } from "react";

import { Alert } from "./ui/alert";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  resetAdminUserPassword,
  updateAdminUser,
  type AdminUser,
} from "../lib/api-admin-users";
import { ApiError } from "../lib/api-client";

interface UserEditModalProps {
  open: boolean;
  user: AdminUser;
  onClose: () => void;
  onSaved: (user: AdminUser) => void;
}

interface FormState {
  displayName: string;
  password: string;
}

export function UserEditModal({ open, user, onClose, onSaved }: UserEditModalProps) {
  const [form, setForm] = useState<FormState>({
    displayName: user.displayName ?? "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm({ displayName: user.displayName ?? "", password: "" });
    setError(null);
  }, [open, user]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmedName = form.displayName.trim();
    const currentName = user.displayName ?? "";
    const nameChanged = trimmedName !== currentName;
    const passwordChanged = form.password.length > 0;

    if (!nameChanged && !passwordChanged) {
      setError("Нечего сохранять — измените имя или задайте новый пароль.");
      return;
    }
    if (passwordChanged && form.password.length < 10) {
      setError("Пароль должен быть не короче 10 символов.");
      return;
    }

    setSubmitting(true);
    try {
      let updated: AdminUser = user;
      if (nameChanged) {
        updated = await updateAdminUser(user.id, { displayName: trimmedName });
      }
      if (passwordChanged) {
        updated = await resetAdminUserPassword(user.id, form.password);
      }
      onSaved(updated);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить изменения");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Редактировать пользователя</DialogTitle>
        </DialogHeader>

        <form
          id="user-edit-form"
          onSubmit={(e) => void onSubmit(e)}
          className="flex flex-col gap-3"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ue-email">Email</Label>
            <Input
              id="ue-email"
              type="email"
              value={user.email}
              readOnly
              disabled
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ue-name">Имя</Label>
            <Input
              id="ue-name"
              type="text"
              value={form.displayName}
              onChange={(e) =>
                setForm((f) => ({ ...f, displayName: e.target.value }))
              }
              disabled={submitting}
              maxLength={120}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ue-password">Новый пароль</Label>
            <Input
              id="ue-password"
              type="password"
              autoComplete="new-password"
              placeholder="оставьте пустым, чтобы не менять"
              minLength={10}
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              disabled={submitting}
            />
            <span className="text-xs text-on-surface-variant">
              Если задан — не короче 10 символов. Пользователю придётся
              переавторизоваться при следующем входе.
            </span>
          </div>

          {error ? <Alert severity="error">{error}</Alert> : null}
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Отмена
          </Button>
          <Button type="submit" form="user-edit-form" disabled={submitting}>
            {submitting ? "Сохраняем…" : "Сохранить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
